import React, { useEffect, useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useApi } from '../../services/api';

type OrchestrationSessionStatus = {
  id: string;
  orchestrationState: string;
  room: { present: boolean; deletedAt: string | null };
  worker: { dispatched: boolean; ready: boolean };
  avatar: { ready: boolean };
  recording: { status: string; objectPresent: boolean; deletedAt: string | null };
  terminal: { eventType: string | null };
  reconciliation: { lastAt: string | null };
  cleanup: { roomDeletedAt: string | null; recordingDeletedAt: string | null };
};

type FlagResult = {
  featureFlag: { enabled: boolean; valid: boolean; code: string };
  automaticReconciliation: boolean;
};

const display = (value: boolean, yes: string, no: string) => value ? yes : no;

const AiInterviewOrchestrationStatusPanel: React.FC = () => {
  const session = useSession();
  const api = useApi();
  const isJason = session?.user?.email?.trim().toLowerCase() === 'jason@intellerview.com';
  const [flag, setFlag] = useState<FlagResult | null>(null);
  const [latest, setLatest] = useState<OrchestrationSessionStatus | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!isJason) return;
    let active = true;
    const load = async () => {
      try {
        const config = await api.get<{ result: FlagResult }>('/ai-interviews/orchestration/config');
        if (!active) return;
        setFlag(config.data.result);
        if (config.data.result.featureFlag.enabled) {
          const diagnostics = await api.get<{ result: OrchestrationSessionStatus[] }>('/ai-interviews/orchestration/diagnostics');
          if (active) setLatest(diagnostics.data.result[0] || null);
        }
      } catch (error: any) {
        if (active) setErrorCode(error?.response?.data?.code || 'status_unavailable');
      }
    };
    void load();
    return () => { active = false; };
  }, [api, isJason]);

  if (!isJason) return null;
  const enabled = Boolean(flag?.featureFlag.enabled);
  const rows = [
    ['Feature flag', flag ? (enabled ? 'Enabled' : flag.featureFlag.valid ? 'Disabled' : 'Invalid') : 'Loading'],
    ['Orchestration', latest?.orchestrationState || (enabled ? 'No persisted session' : 'Foundation disabled')],
    ['Room', latest ? display(latest.room.present, 'Present', latest.room.deletedAt ? 'Deleted' : 'Absent') : 'Not started'],
    ['Worker / avatar', latest ? `${latest.worker.ready ? 'Worker ready' : latest.worker.dispatched ? 'Worker dispatched' : 'Worker idle'} · ${latest.avatar.ready ? 'Avatar ready' : 'Avatar idle'}` : 'Not started'],
    ['Recording', latest ? `${latest.recording.status} · ${latest.recording.objectPresent ? 'Private object present' : latest.recording.deletedAt ? 'Object deleted' : 'No object'}` : 'Not started'],
    ['Terminal', latest?.terminal.eventType || 'Not terminal'],
    ['Reconciliation', latest?.reconciliation.lastAt ? `Last run ${latest.reconciliation.lastAt}` : 'Explicit run not performed'],
    ['Cleanup', latest ? `${latest.cleanup.roomDeletedAt ? 'Room deleted' : 'Room retained'} · ${latest.cleanup.recordingDeletedAt ? 'Recording deleted' : 'Recording retained'}` : 'Not started'],
  ];

  return (
    <section className="rounded-[8px] border border-[#99f6e4] bg-[#f0fdfa] px-5 py-4 shadow-sm" aria-label="AI interview orchestration status">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0f766e]">Production orchestration foundation</p>
          <h2 className="mt-1 text-[16px] font-bold text-[#0f172a]">Operational status</h2>
        </div>
        <span className="rounded-full border border-[#5eead4] bg-white px-2.5 py-1 text-[11px] font-bold text-[#115e59]">
          Jason-only · default off
        </span>
      </div>
      {errorCode ? <p className="mt-3 text-[12px] text-[#991b1b]">Status unavailable ({errorCode}).</p> : null}
      <dl className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-[6px] border border-[#ccfbf1] bg-white px-3 py-2">
            <dt className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#64748b]">{label}</dt>
            <dd className="mt-1 text-[12px] font-semibold text-[#334155]">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-[11px] text-[#64748b]">
        Reconciliation is explicit only. This panel never returns join tokens, signed playback URLs, transcripts, prompts, or provider payloads.
      </p>
    </section>
  );
};

export default AiInterviewOrchestrationStatusPanel;
